import { ErrorBoundary } from '@sentry/react'
import cx from 'classnames'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { DocRenderTracker } from 'common/Prose/EditorRenderer/DocRenderTracker'
import { EditorRenderer } from 'common/Prose/EditorRenderer/EditorRenderer'
import { PortalTOC } from 'common/Prose/components/TOC/PortalTOC'
import { ReactNodeViewPortalsProvider } from 'common/Prose/nodeViews/ReactNodeView/ReactNodeViewPortals'
import { useHandbookDocNavigator } from 'components/Handbook2_0/useHandbookDocNavigator'
import * as Layout from 'components/Layout'
import { ConfirmationModal } from 'components/NavRightClickMenu/components/ActionModal/ConfirmationModal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { buildArticleStyleCSSProperties } from 'proseShared/utils'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { ArticleFlat, Layer, RootState } from 'types'
import { useAccessRequestLocationHandler } from 'hooks/useAccessRequestLocationHandler'
import { NerudaDocEditor } from '../NerudaDocEditor/NerudaDocEditor'
import { RightRail } from '../RightRail/RightRail'
import { ViewEditableContext } from '../ViewEditableContextProvider/ViewEditableContextProvider'
import Styles from './NerudaEditor.module.scss'
import { WriterFormatToolbarContainer } from 'common/Prose/Toolbar/ToolbarBravo/WriterFormatToolbar/WriterFormatToolbarContainer'
import { EditorProps } from 'prosemirror-view'
import { ArticlePostPreview } from 'components/ArticlePostPreview/ArticlePostPreview'
import { PostPreviewHeaderV2 } from 'components/ArticlePostPreview/PostPreviewHeader/PostPreviewHeaderV2'
import { observer } from 'mobx-react-lite'
import { MobileWriterFormatBar } from 'common/Prose/Toolbar/MobileWriterFormatBar/MobileWriterFormatBar'
import { AssigneeType } from 'api/clients/PropertyClient'
import { useHideProperties } from 'hooks/useHideProperties'
import { ConditionalAssignedProperties } from 'components/ConditionalAssignedProperties/ConditionalAssignedProperties'
import { useFinishedRenderingNativeMessage } from 'hooks/useFinishedRenderingNativeMessage'

interface Props {
  article: ArticleFlat
  layer: Layer
  readOnly: boolean
  inCommentMode?: boolean
  mode: 'read' | 'edit'
  rightRailProps?: {
    show: boolean
    showSnippetsRail: boolean
    docModeIsEditor: boolean
    leftRailOpen: boolean
  }
  editorProps?: EditorProps
}

const NerudaEditor = observer(
  ({ article, layer, inCommentMode, readOnly, mode, rightRailProps, editorProps }: Props) => {
    const { isMobile: showMobileToolbar } = useResponsiveDesign()
    useHandbookDocNavigator(readOnly)
    useAccessRequestLocationHandler(article, !readOnly)
    const hideProperties = useHideProperties()
    const [propertiesLoaded, setPropertiesLoaded] = useState(false)

    const articleMeta = useSelector(({ articleMeta }: RootState) => articleMeta[article.id])

    const [editable, setEditable] = useState(false)

    useFinishedRenderingNativeMessage()

    return (
      <div className={Styles.textContainer}>
        <ViewEditableContext.Provider value={{ editable, setEditable }}>
          <ReactNodeViewPortalsProvider>
            <NerudaDocEditor
              article={article}
              layer={layer}
              inCommentMode={inCommentMode}
              readOnly={readOnly}
              editorProps={editorProps}
            >
              <ConfirmationModal />
              {!readOnly && !inCommentMode && showMobileToolbar && (
                <Layout.MobileWriterToolbar>
                  <MobileWriterFormatBar />
                </Layout.MobileWriterToolbar>
              )}
              <PortalTOC />
              <Layout.WriterToolbar>
                <WriterFormatToolbarContainer />
              </Layout.WriterToolbar>

              {articleMeta && !hideProperties && (
                <Layout.PostPreview>
                  <ArticlePostPreview />
                </Layout.PostPreview>
              )}
              {articleMeta && hideProperties && (
                <Layout.PostPreview>
                  <PostPreviewHeaderV2 />
                </Layout.PostPreview>
              )}

              {rightRailProps && (
                <Layout.RightColumn>
                  <RightRail article={article} />
                </Layout.RightColumn>
              )}

              <div className={Styles.container}>
                <div
                  className={cx(Styles.contentContainer)}
                  style={buildArticleStyleCSSProperties(
                    articleMeta?.workspace_article_style,
                    articleMeta?.article_style,
                  )}
                >
                  <DocRenderTracker>
                    <EditorRenderer autoFocus />
                  </DocRenderTracker>
                </div>
              </div>
            </NerudaDocEditor>
          </ReactNodeViewPortalsProvider>
        </ViewEditableContext.Provider>

        <ConditionalAssignedProperties
          assignee={{
            id: article.id,
            type: AssigneeType.Article,
          }}
        >
          {({ loadMore }) => {
            if (!propertiesLoaded) {
              loadMore()
              setPropertiesLoaded(true)
            }
            return null
          }}
        </ConditionalAssignedProperties>
      </div>
    )
  },
)

const WithError = (props: Props) => (
  <ErrorBoundary
    onError={() =>
      showErrorMessageToast(
        'Failed to display document. Please try to refresh the page. If this error persists, please contact support.',
      )
    }
  >
    <NerudaEditor {...props} />
  </ErrorBoundary>
)

export { WithError as NerudaEditor }
